import React from 'react';
import { Query } from 'react-apollo';
import { withStyles } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import { withRouter } from 'react-router';
import SummaryCard from './summaryCard';
import ContactIcon from './contact.svg';
import DocumentIcon from './document.svg';
import HouseIcon from './house.svg';
import PersonIcon from './person.svg';
import styles from './demo.styles';
import { compose, get } from '../../../utility';
import { ROLE_ID, ROLE_CATEGORY, STATUS } from '../../../constants';
import adminDashboardSuperAdminCounts from '../../api/adminDashboardSuperAdminCounts.query.graphql';
import adminDashboardBrokerageAdminCounts from '../../api/adminDashboardBrokerageAdminCounts.query.graphql';
import { withAppUser } from '../../../shared/authorization/userConsumer';

function isSuperAdminUser(user) {
  const nodes = get(user, 'systemAclsByUserId.nodes', []);
  return nodes.some(acl => Number(acl.roleId) === ROLE_ID.SUPER_ADMIN);
}

function sumNestedBrokerageAclTotals(data, key) {
  return get(data, `${key}.nodes`, []).reduce((sum, n) => {
    const tc = get(n, 'brokerageByBrokerageId.brokerageAclsByBrokerageId.totalCount', 0);
    return sum + Number(tc || 0);
  }, 0);
}

const DemoC = ({
  classes,
  history,
  user,
}: {
  classes: Object,
  history: Object,
  user: ?Object,
}) => {
  const isSuper = user && isSuperAdminUser(user);

  if (!user) {
    return (
      <div className={classes.container}>
        <Typography color="textSecondary">Loading…</Typography>
      </div>
    );
  }

  if (isSuper) {
    return (
      <Query query={adminDashboardSuperAdminCounts} fetchPolicy="network-only">
        {({ loading, error, data }) => {
          if (error) {
            return (
              <Typography color="error" className={classes.error}>
                Could not load dashboard statistics.
              </Typography>
            );
          }
          const brokerages = loading ? 0 : get(data, 'brokerages.totalCount', 0);
          const admins = loading ? 0 : get(data, 'admins.totalCount', 0);
          const agents = loading ? 0 : get(data, 'agents.totalCount', 0);
          const invites = loading ? 0 : get(data, 'pendingInvites.totalCount', 0);
          return (
            <div className={classes.container}>
              <div className={classes.content}>
                <SummaryCard
                  icon={HouseIcon}
                  number={brokerages}
                  plainNumber
                  text="Brokerage offices"
                  onClick={() => history.push('/admin/brokerage-management')}
                />
                <SummaryCard
                  icon={ContactIcon}
                  number={admins}
                  plainNumber
                  text="Admins"
                  onClick={() => history.push('/admin/admin-management')}
                />
                <SummaryCard
                  icon={PersonIcon}
                  number={agents}
                  plainNumber
                  text="Agents"
                  onClick={() => history.push('/admin/agent-management')}
                />
                <SummaryCard
                  icon={DocumentIcon}
                  number={invites}
                  plainNumber
                  text="Pending invites"
                  onClick={() => history.push('/admin/invites')}
                />
              </div>
              <Typography className={classes.footerNote} color="textSecondary">
                Figures are live from your database. Use the sidebar to open detailed lists.
              </Typography>
            </div>
          );
        }}
      </Query>
    );
  }

  const variables = {
    brokerageAclCondition: {
      userId: Number(user.id),
      roleCategory: ROLE_CATEGORY.ADMIN,
    },
    agentCondition: {
      roleCategory: ROLE_CATEGORY.AGENT,
    },
    adminCondition: {
      roleCategory: ROLE_CATEGORY.ADMIN,
    },
    inviteCondition: {
      inviteStatus: STATUS.PENDING,
      requestedById: Number(user.id),
    },
  };

  return (
    <Query
      query={adminDashboardBrokerageAdminCounts}
      variables={variables}
      fetchPolicy="network-only"
    >
      {({ loading, error, data }) => {
        if (error) {
          return (
            <Typography color="error" className={classes.error}>
              Could not load dashboard statistics.
            </Typography>
          );
        }
        const brokerages = loading ? 0 : get(data, 'brokerages.totalCount', 0);
        const agents = loading ? 0 : sumNestedBrokerageAclTotals(data, 'agentSlots');
        const admins = loading ? 0 : sumNestedBrokerageAclTotals(data, 'adminSlots');
        const invites = loading ? 0 : get(data, 'myPendingInvites.totalCount', 0);
        return (
          <div className={classes.container}>
            <div className={classes.content}>
              <SummaryCard
                icon={HouseIcon}
                number={brokerages}
                plainNumber
                text="Brokerage offices"
                onClick={() => history.push('/admin/brokerage-management')}
              />
              <SummaryCard
                icon={ContactIcon}
                number={admins}
                plainNumber
                text="Admins"
                onClick={() => history.push('/admin/admin-management')}
              />
              <SummaryCard
                icon={PersonIcon}
                number={agents}
                plainNumber
                text="Agents"
                onClick={() => history.push('/admin/agent-management')}
              />
              <SummaryCard
                icon={DocumentIcon}
                number={invites}
                plainNumber
                text="Pending invites"
                onClick={() => {}}
              />
            </div>
            <Typography className={classes.footerNote} color="textSecondary">
              Counts reflect brokerages you manage. Figures are loaded from your database.
            </Typography>
          </div>
        );
      }}
    </Query>
  );
};

export default compose(
  withStyles(styles),
  withRouter,
  withAppUser,
)(DemoC);
